/* AL RIOTBALL PLAYOFFS — a 10-team bracket to the chip. All ten franchises
   qualify; seeds 7-10 fight a two-game PLAY-IN while seeds 1-6 hold a bye.
   The player's matches are played (tier ramps each round), the rest of the
   bracket is simmed by the mesh. Lose and you watch the bracket move on. */
import { TEAM_ORDER } from "./constants";

export const PLAYOFF_ROUNDS = ["PLAY-IN", "QUARTERFINAL", "SEMIFINAL", "FINAL"];
export const ROUND_TIERS = ["easy", "easy", "normal", "brutal"];
export const PLAYOFF_CHAMPION_PTS = 40;
const FINAL_ROUND = PLAYOFF_ROUNDS.length - 1;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* CPU-vs-CPU sim — no draws in the playoffs; a dash of form, mostly chaos */
const simMatch = (m) => {
  let home = Math.floor(Math.random() * 4);
  let away = Math.floor(Math.random() * 4);
  if (home === away) away = (away + 1) % 4;
  return { ...m, homeScore: home, awayScore: away, played: true, winner: home > away ? m.home : m.away };
};

const nextRoundMatches = (winners, playerTeam, playerAlive, roundIdx, seedOf) => {
  const matches = [];
  for (let i = 0; i < winners.length; i += 2) {
    matches.push({
      home: winners[i],
      away: winners[i + 1],
      homeSeed: seedOf[winners[i]] ?? null, // seeds ride through the bracket
      awaySeed: seedOf[winners[i + 1]] ?? null,
      homeScore: null,
      awayScore: null,
      played: false,
      winner: null,
      isPlayer: playerAlive && (winners[i] === playerTeam || winners[i + 1] === playerTeam),
      id: `r${roundIdx}-${i / 2}`,
    });
  }
  return matches;
};

/* quarterfinal slots after the play-in: 1v(8/9 winner) · 4v5 · 3v6 · 2v(7/10 winner) */
const qfFromPlayIn = (winners, p, playerAlive) => {
  const s = p.seeds;
  const slots = [[s[0], winners[1]], [s[3], s[4]], [s[2], s[5]], [s[1], winners[0]]];
  return slots.map(([h, a], i) => ({
    home: h,
    away: a,
    homeSeed: p.seedOf[h] ?? null,
    awaySeed: p.seedOf[a] ?? null,
    homeScore: null,
    awayScore: null,
    played: false,
    winner: null,
    isPlayer: playerAlive && (h === p.playerTeam || a === p.playerTeam),
    id: `qf-${i}`,
  }));
};

/* the round after `p.roundIdx` — play-in resolves into fixed QF seed slots */
const buildNextRound = (p, winners, playerAlive) =>
  p.roundIdx === 0
    ? qfFromPlayIn(winners, p, playerAlive)
    : nextRoundMatches(winners, p.playerTeam, playerAlive, p.roundIdx + 1, p.seedOf);

/* a true upset — the winner's seed number beats the favorite by 3+ lines */
const upsetsIn = (round) =>
  round
    .filter((m) => m.played && m.winner && m.homeSeed && m.awaySeed)
    .map((m) => {
      const loser = m.winner === m.home ? m.away : m.home;
      return {
        winner: m.winner,
        loser,
        winnerSeed: m.winner === m.home ? m.homeSeed : m.awaySeed,
        loserSeed: m.winner === m.home ? m.awaySeed : m.homeSeed,
        winnerGoals: m.winner === m.home ? m.homeScore : m.awayScore,
        loserGoals: m.winner === m.home ? m.awayScore : m.homeScore,
      };
    })
    .filter((u) => u.winnerSeed - u.loserSeed >= 3);

export const buildPlayoff = (playerTeam, records = null) => {
  // seed from the live season standings when the league has records — ALL TEN
  // franchises make the bracket; seeds 7-10 fight the play-in, 1-6 hold a bye
  const hasRecords = records && Object.keys(records).length > 0;
  let seeds;
  if (hasRecords) {
    seeds = [...TEAM_ORDER].sort(
      (a, b) => (records[b] || 0) - (records[a] || 0) || TEAM_ORDER.indexOf(a) - TEAM_ORDER.indexOf(b)
    );
  } else {
    seeds = [playerTeam, ...shuffle(TEAM_ORDER.filter((t) => t !== playerTeam))];
  }
  const playerSeed = seeds.indexOf(playerTeam) + 1;
  const seedOf = Object.fromEntries(seeds.map((t, i) => [t, i + 1]));
  const playIn = [[6, 9], [7, 8]].map(([h, a], i) => ({ // #7v#10 · #8v#9
    home: seeds[h],
    away: seeds[a],
    homeSeed: h + 1,
    awaySeed: a + 1,
    homeScore: null,
    awayScore: null,
    played: false,
    winner: null,
    isPlayer: seeds[h] === playerTeam || seeds[a] === playerTeam,
    id: `pi-${i}`,
  }));
  return { rounds: [playIn, [], [], []], roundIdx: 0, playerTeam, playerSeed, seedOf, seeds, seededByRecords: hasRecords, playerAlive: true, finished: false, champion: null, lastUpsets: [] };
};

/* the match the player has to play right now, and who stands across from them */
export const playerMatch = (p) =>
  p && p.playerAlive && !p.finished
    ? p.rounds[p.roundIdx].find((m) => m.isPlayer && !m.played) || null
    : null;

export const playerOpponent = (p) => {
  const m = playerMatch(p);
  if (!m) return null;
  return m.home === p.playerTeam ? m.away : m.home;
};

/* record the player's finished match, sim the rest of the round, build the next */
export const resolvePlayerMatch = (p, playerScore, cpuScore) => {
  const playerWon = playerScore > cpuScore;
  const next = {
    ...p,
    playerAlive: playerWon,
    rounds: p.rounds.map((r) => r.map((m) => ({ ...m }))),
  };
  const round = next.rounds[next.roundIdx];
  const pm = round.find((m) => m.isPlayer);
  pm.homeScore = pm.home === p.playerTeam ? playerScore : cpuScore;
  pm.awayScore = pm.away === p.playerTeam ? playerScore : cpuScore;
  pm.played = true;
  pm.winner = playerWon ? p.playerTeam : pm.home === p.playerTeam ? pm.away : pm.home;
  for (const m of round) if (!m.played) Object.assign(m, simMatch(m));
  next.lastUpsets = upsetsIn(round);
  const winners = round.map((m) => m.winner);
  if (next.roundIdx === FINAL_ROUND) {
    next.finished = true;
    next.champion = winners[0];
    return next;
  }
  next.rounds[next.roundIdx + 1] = buildNextRound(next, winners, playerWon);
  next.roundIdx += 1;
  return next;
};

/* out of the bracket — or just curious: sim everything that's left */
export const simToEnd = (p) => {
  let next = { ...p, rounds: p.rounds.map((r) => r.map((m) => ({ ...m }))) };
  const upsets = [];
  while (!next.finished) {
    const round = next.rounds[next.roundIdx];
    if (!round.length) break;
    for (const m of round) if (!m.played) Object.assign(m, simMatch(m));
    upsets.push(...upsetsIn(round));
    const winners = round.map((m) => m.winner);
    if (next.roundIdx === FINAL_ROUND) {
      next.finished = true;
      next.champion = winners[0];
      break;
    }
    next.rounds[next.roundIdx + 1] = buildNextRound(next, winners, false);
    next.roundIdx += 1;
  }
  next.lastUpsets = [...(p.lastUpsets || []), ...upsets];
  return next;
};

/* when the player is IN the play-in, settle the sibling game up front so the
   bracket opens with one slot already decided */
export const simSiblingPlayIn = (p) => {
  if (p.roundIdx !== 0 || p.finished) return p;
  const round = p.rounds[0];
  if (!round.some((m) => m.isPlayer) || round.every((m) => m.played)) return p;
  const next = { ...p, rounds: p.rounds.map((r) => r.map((m) => ({ ...m }))) };
  for (const m of next.rounds[0]) if (!m.played && !m.isPlayer) Object.assign(m, simMatch(m));
  next.lastUpsets = upsetsIn(next.rounds[0]);
  return next;
};

/* first-round byes — seeds 1-6 skip the play-in: sim it so their quarterfinal
   is set before their first kick. Only runs while the player is alive and
   waiting; a dead bracket moves on through simToEnd instead */
export const advanceByes = (p) => {
  let next = p;
  while (!next.finished && next.playerAlive && !playerMatch(next)) {
    const round = next.rounds[next.roundIdx];
    if (!round || !round.length) break;
    next = { ...next, rounds: next.rounds.map((r) => r.map((m) => ({ ...m }))) };
    for (const m of next.rounds[next.roundIdx]) if (!m.played) Object.assign(m, simMatch(m));
    next.lastUpsets = upsetsIn(next.rounds[next.roundIdx]);
    const winners = next.rounds[next.roundIdx].map((m) => m.winner);
    if (next.roundIdx === FINAL_ROUND) {
      next.finished = true;
      next.champion = winners[0];
      break;
    }
    next.rounds[next.roundIdx + 1] = buildNextRound(next, winners, next.playerAlive);
    next.roundIdx += 1;
  }
  return next;
};
